import { memo } from 'react';

import { ChevronRightIcon } from '@air/design-icons';
import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { type WorkspaceAgentIconComponent } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/types';

const workspaceAgentQuickReplyChip = tailwindVariants({
  defaultVariants: {
    isBorderless: false,
    variant: 'outlined',
  },
  slots: {
    chevron:
      'inline-flex w-0 min-w-0 shrink-0 items-center overflow-hidden opacity-0 transition-[width,margin,opacity] duration-150 ease-out group-hover:ml-1 group-hover:w-3 group-hover:opacity-100 group-focus-visible:ml-1 group-focus-visible:w-3 group-focus-visible:opacity-100',
    icon: 'flex size-6 shrink-0 items-center justify-center',
    label: 'min-w-0 truncate font-medium',
    labelRow: 'flex min-w-0 items-center text-12 leading-4',
    root: 'group inline-flex max-w-full items-center gap-1 border py-1 pr-3 pl-2 text-left transition-colors focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8 disabled:cursor-not-allowed disabled:opacity-45',
  },
  variants: {
    isBorderless: {
      false: {},
      true: {
        root: 'relative z-1 border-transparent enabled:active:border-transparent enabled:hover:border-transparent',
      },
    },
    variant: {
      ghost: {
        chevron: 'ads:text-icon-default text-gray-11',
        icon: 'ads:text-icon-default text-gray-11',
        label: 'whitespace-normal ads:text-secondary text-gray-11',
        root: 'min-w-0 rounded-8 border-transparent bg-transparent enabled:active:border-transparent enabled:active:ads:bg-hover-subtle enabled:active:bg-gray-4 enabled:hover:border-transparent enabled:hover:ads:bg-hover-subtle enabled:hover:bg-gray-5',
      },
      outlined: {
        chevron: 'ads:text-icon-subtle text-gray-9',
        icon: 'ads:text-icon-subtle text-gray-9',
        label: 'ads:text-primary text-gray-12',
        root: 'rounded-full ads:border-default border-gray-4 ads:bg-base bg-gray-1 ads:enabled:active:border-hover-input enabled:active:border-gray-5 ads:enabled:active:bg-hover-subtle enabled:active:bg-gray-2 ads:enabled:hover:border-hover-input enabled:hover:border-gray-5 ads:enabled:hover:bg-hover-subtle enabled:hover:bg-gray-2',
      },
    },
  },
  compoundVariants: [
    {
      // The shimmer is a spinning fill behind a 1px gap. Ghost is otherwise transparent, so without
      // this ground the cone paints the whole chip instead of a ring.
      class: { root: 'ads:bg-base bg-gray-1' },
      isBorderless: true,
      variant: 'ghost',
    },
  ],
});

export type WorkspaceAgentQuickReplyChipVariant = NonNullable<
  VariantProps<typeof workspaceAgentQuickReplyChip>['variant']
>;

export type WorkspaceAgentQuickReplyChipProps = {
  className?: string;
  'data-testid'?: string;
  icon: WorkspaceAgentIconComponent;
  /** The shimmer shell draws the border, so the chip's own stroke stays out of the way. */
  isBorderless?: boolean;
  isDisabled?: boolean;
  label: string;
  onSelect: () => void;
  suggestionId?: string;
  variant?: WorkspaceAgentQuickReplyChipVariant;
};

/**
 * A suggested next prompt. The trailing chevron is revealed on hover and focus rather than always
 * shown, so a row of these reads as text until the user aims at one.
 */
export const WorkspaceAgentQuickReplyChip = memo(
  ({
    className,
    'data-testid': testId,
    icon: Icon,
    isBorderless = false,
    isDisabled = false,
    label,
    onSelect,
    suggestionId,
    variant,
  }: WorkspaceAgentQuickReplyChipProps) => {
    const classes = workspaceAgentQuickReplyChip({ isBorderless, variant });

    return (
      <button
        className={classes.root({ className })}
        data-suggestion-id={suggestionId}
        data-testid={testId}
        disabled={isDisabled}
        onClick={onSelect}
        type="button"
      >
        <span className={classes.icon()}>
          <Icon size="sm" />
        </span>
        <span className={classes.labelRow()}>
          <span className={classes.label()}>{label}</span>
          {/*
            The chevron grows out of zero width so the label does not shift when it appears, which needs
            width, margin and opacity transitioned together — there is no config token for that set.
          */}
          <span aria-hidden className={classes.chevron()}>
            <ChevronRightIcon className="shrink-0" size="xs" />
          </span>
        </span>
      </button>
    );
  },
);

WorkspaceAgentQuickReplyChip.displayName = 'WorkspaceAgentQuickReplyChip';
